import React from "react";

// Material-UI
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Button
} from "@material-ui/core";
import { Close, Delete, Warning } from "@material-ui/icons";

// image
import noImage from "../../assets/images/noImage.png";

const DeleteReelConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    selectedReason,
    setSelectedReason,
    itemData,
    itemType = "video",
    title = "Delete Video"
}) => {
    const deleteReasons = [
        {
            value: "inappropriate_content",
            title: "Inappropriate content",
            description: "Contains nudity, sexual content, or explicit imagery."
        },
        {
            value: "violent_content",
            title: "Violent or graphic content",
            description: "Depicts violence, abuse, or disturbing visuals."
        },
        {
            value: "hate_speech",
            title: "Hate speech or abusive behavior",
            description: "Promotes hate against individuals or groups based on race, religion, gender, etc."
        },
        {
            value: "harassment",
            title: "Harassment or bullying",
            description: "Targets or threatens other users in a harmful way."
        },
        {
            value: "misinformation",
            title: "Misinformation or misleading content",
            description: "Spreads false claims or deceptive information."
        },
        {
            value: "spam",
            title: "Spam or repetitive content",
            description: "Unwanted promotional material or repeated posts."
        },
        {
            value: "copyright",
            title: "Copyright infringement",
            description: "Uses music, video, or other materials without proper rights."
        },
        {
            value: "fake_identity",
            title: "Fake or misleading identity",
            description: "Pretends to be someone else or impersonates individuals."
        },
        {
            value: "illegal_content",
            title: "Illegal content",
            description: "Shows or promotes illegal activities (e.g., drugs, weapons, crimes)."
        },
        {
            value: "child_endangerment",
            title: "Child endangerment",
            description: "Involves minors in unsafe, abusive, or exploitative situations."
        },
        {
            value: "low_quality",
            title: "Low quality or broken video",
            description: "Unwatchable due to extreme pixelation, errors, or black screens."
        },
        {
            value: "community_guidelines",
            title: "Against community guidelines",
            description: "General violation of app policies or ethical standards."
        }
    ];

    const handleConfirm = () => {
        onConfirm(selectedReason, deleteReasons.find(r => r.value === selectedReason)?.title);
    };

    const renderThumbnail = () => {
        if (!itemData) return null;

        return (
            <div className="mb-4 text-center">
                <Typography variant="subtitle2" className="mb-2" style={{ color: "#e0e0e0" }}>
                    {itemType.charAt(0).toUpperCase() + itemType.slice(1)} to be deleted:
                </Typography>
                <img
                    src={itemData.thumbnail ? itemData.thumbnail : noImage}
                    alt={`${itemType} thumbnail`}
                    style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "2px solid #dc3545",
                        boxShadow: "0 4px 8px rgba(220, 53, 69, 0.3)"
                    }}
                />
                {itemData.location && (
                    <Typography variant="caption" className="d-block mt-2" style={{ color: "#b0b0b0" }}>
                        Location: {itemData.location}
                    </Typography>
                )}
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="delete-dialog-title"
            disableBackdropClick
            disableEscapeKeyDown
            disableScrollLock
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="delete-dialog-title">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Warning className="text-danger" style={{ marginRight: "8px" }} />
                        <span className="text-danger font-weight-bold h5" style={{ color: "#dc3545" }}>{title}</span>
                    </div>
                    <IconButton onClick={onClose}>
                        <Close style={{ color: "#dc3545" }} />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent>
                <div className="modal-body pt-1 px-1 pb-3">
                    {/* Item thumbnail preview */}
                    {renderThumbnail()}

                    <Typography variant="body2" className="mb-4" style={{ color: "#e0e0e0" }}>
                        Please select a reason for deleting this {itemType}. This action cannot be undone.
                    </Typography>

                    <RadioGroup
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        name="deleteReason"
                    >
                        {deleteReasons.map((reason) => (
                            <div
                                key={reason.value}
                                className={`mb-2 p-3 border rounded`}
                                style={{
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    backgroundColor: selectedReason === reason.value ? "rgba(220, 53, 69, 0.15)" : "transparent",
                                    borderColor: selectedReason === reason.value ? "#dc3545" : "#d0d0d0",
                                    borderWidth: selectedReason === reason.value ? "2px" : "1px"
                                }}
                                onClick={() => setSelectedReason(reason.value)}
                            >
                                <FormControlLabel
                                    value={reason.value}
                                    control={<Radio style={{ color: selectedReason === reason.value ? "#dc3545" : "#6c757d" }} />}
                                    label={
                                        <div>
                                            <Typography variant="subtitle2" className="font-weight-bold" style={{ color: "#ffffff", fontSize: "14px" }}>
                                                {reason.title}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: selectedReason === reason.value ? "#e0e0e0" : "#9e9e9e", fontSize: "12px", marginTop: "4px", display: "block" }}>
                                                {reason.description}
                                            </Typography>
                                        </div>
                                    }
                                    style={{ width: "100%", margin: 0 }}
                                />
                            </div>
                        ))}
                    </RadioGroup>
                </div>
            </DialogContent>

            <DialogActions style={{ padding: "15px 24px" }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                        color: "#e0e0e0",
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        backgroundColor: "transparent"
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!selectedReason}
                    color="secondary"
                    variant="contained"
                    className="btn-danger"
                    startIcon={<Delete />}
                    style={{
                        backgroundColor: selectedReason ? "#dc3545" : "#6c757d",
                        color: "white"
                    }}
                >
                    Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteReelConfirmationDialog;
